import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axiosInstance, { ensureCsrfToken } from '../utils/axiosInstance'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMoney } from './currenciesStore'
import { useMemo, useEffect } from 'react'

// Debounce function to prevent multiple API calls
let debounceTimer = null;
const debounce = (fn, delay = 300) => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(fn, delay);
};

const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],
      isLoading: false,
      bestDiscount: null,
      isDiscountLoading: false,
      
      // Fetch cart items from backend
      fetchCartItems: async () => {
        set({ isLoading: true })
        try {
          // Ensure CSRF token is set using the utility function
          // Removed: await ensureCsrfToken(); - This will be handled in App.js only
          
          const response = await axiosInstance.get('/api/cart-items')
          set({ items: response.data.data || [], isLoading: false })
          return true
        } catch (error) {
          console.error('Error fetching cart items:', error)
          set({ isLoading: false })
          throw error
        }
      },

      // Fetch best discount for current cart
      fetchBestDiscount: async () => {
        const items = get().items
        if (!items.length) {
          set({ bestDiscount: null })
          return null
        }

        set({ isDiscountLoading: true })
        
        // Use debounce to prevent multiple API calls
        return new Promise((resolve) => {
          debounce(async () => {
            try {
              // No need to get the CSRF cookie again since it should already be set from fetchCartItems
              
              // Transform cart items to the required API format
              const cart_items = items.map(item => ({
                product_id: item.product.id,
                quantity: item.quantity
              }))
              
              // Send cart items with POST request
              const response = await axiosInstance.post('/api/cart-discount', {
                cart_items
              })
              
              const discount = response.data.data || null
              set({ bestDiscount: discount, isDiscountLoading: false })
              resolve(discount)
            } catch (error) {
              console.error('Error fetching best discount:', error)
              set({ isDiscountLoading: false })
              resolve(null)
            }
          });
        });
      },

      // Add item to cart with quantity
      addToCart: async (product, quantity = 1) => {
        const items = get().items
        const existingItem = items.find(item => item.product.id === product.id)
        const newQuantity = existingItem ? existingItem.quantity + quantity : quantity

        try {
          // No need to request a new CSRF token here
          await axiosInstance.post('/api/cart-items', {
            product_id: product.id,
            quantity: newQuantity
          })

          if (existingItem) {
            const updatedItems = items.map(item =>
              item.product.id === product.id
                ? { ...item, quantity: newQuantity }
                : item
            )
            set({ items: updatedItems })
          } else {
            set({ items: [...items, { product, quantity }] })
          }
          
          // Fetch best discount after cart update
          get().fetchBestDiscount()
          return true
        } catch (error) {
          console.error('Error adding item to cart:', error)
          // Make sure we're not losing response data for 422 errors
          if (error.response && error.response.status === 422) {
            // Explicitly ensure the error object has all the necessary properties
            const enhancedError = new Error(error.message || 'Validation Error');
            enhancedError.response = error.response;
            throw enhancedError;
          }
          throw error
        }
      },

      // Remove item from cart
      removeFromCart: async (productId) => {
        try {
          // No need to request a new CSRF token here
          await axiosInstance.delete(`/api/cart-items/${productId}`)

          const items = get().items
          set({ items: items.filter(item => item.product.id !== productId) })
          
          // Fetch best discount after cart update
          const updatedItems = items.filter(item => item.product.id !== productId)
          if (updatedItems.length === 0) {
            set({ bestDiscount: null })
          } else {
            get().fetchBestDiscount()
          }
          return true
        } catch (error) {
          console.error('Error removing item from cart:', error)
          throw error
        }
      },

      // Update item quantity
      updateQuantity: async (productId, quantity) => {
        try {
          // No need to request a new CSRF token here
          await axiosInstance.post('/api/cart-items', {
            product_id: productId,
            quantity
          })

          const items = get().items
          const updatedItems = items.map(item =>
            item.product.id === productId
              ? { ...item, quantity }
              : item
          )
          set({ items: updatedItems })
          
          // Fetch best discount after cart update
          get().fetchBestDiscount()
          return true
        } catch (error) {
          console.error('Error updating item quantity:', error)
          throw error
        }
      },

      // Clear cart
      clearCart: () => {
        set({ items: [], bestDiscount: null })
      },

      // Get cart total
      getTotal: () => {
        const items = get().items
        const bestDiscount = get().bestDiscount
        
        // Calculate subtotal
        const subtotal = items.reduce((total, item) => {
          // Add null check to handle cases where item.product might be null
          if (!item || !item.product) return total;
          
          const price = item.product.computed_price || 0;
          return total + (price * item.quantity)
        }, 0)
        
        // Apply discount if available
        if (bestDiscount && bestDiscount.amount > 0) {
          return subtotal - bestDiscount.amount
        }
        
        return subtotal
      },
      
      // Get subtotal (without discount)
      getSubtotal: () => {
        const items = get().items
        return items.reduce((total, item) => {
          // Add null check to handle cases where item.product might be null
          if (!item || !item.product) return total;
          
          const price = item.product.computed_price || 0;
          return total + (price * item.quantity)
        }, 0)
      },

      // Get total items count
      getItemsCount: () => {
        const items = get().items
        return items.reduce((count, item) => count + item.quantity, 0)
      }
    }),
    {
      name: 'shopping-cart', // unique name for localStorage
    }
  )
)

export const useCart = () => {
  const cartStore = useCartStore()
  const { t } = useTranslation()

  const addToCart = async (product, quantity = 1) => {
    try {
      await cartStore.addToCart(product, quantity)
      toast.success(t('cart.success'))
    } catch (error) {
      // Check if it's a validation error (422)
      if (error.response && error.response.status === 422) {
        const errorMessage = error.response.data.message || error.response.data.data?.error || t('cart.error')
        toast.error(errorMessage)
      } else {
        toast.error(t('cart.error'))
      }
    }
  }

  const removeFromCart = async (productId) => {
    try {
      await cartStore.removeFromCart(productId)
      toast.success(t('cart.deleted'))
    } catch (error) {
      toast.error(t('cart.deleted-error'))
      throw error
    }
  }

  const updateQuantity = async (productId, quantity) => {
    try {
      await cartStore.updateQuantity(productId, quantity)
      toast.success(t('cart.updated'))
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const errorMessage = error.response.data.message || error.response.data.data?.error || t('cart.error')
        toast.error(errorMessage)
      } else {
        toast.error(t('cart.error'))
      }
      throw error
    }
  }

  return {
    items: cartStore.items,
    isLoading: cartStore.isLoading,
    bestDiscount: cartStore.bestDiscount,
    isDiscountLoading: cartStore.isDiscountLoading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart: cartStore.clearCart,
    getTotal: cartStore.getTotal,
    getSubtotal: cartStore.getSubtotal,
    getItemsCount: cartStore.getItemsCount,
    fetchCartItems: cartStore.fetchCartItems,
    fetchBestDiscount: cartStore.fetchBestDiscount,
  }
}

export const useCartTotal = () => {
  const total = useCartStore(state => state.getTotal())
  const { money } = useMoney()
  
  return useMemo(() => {
    if(total > 0) {
      return [total, money(total)]
    }
    return [0, money(0)]
  }, [total, money])
}

// Hook to get cart subtotal and discount information
export const useCartDiscount = () => {
  const subtotal = useCartStore(state => state.getSubtotal())
  const total = useCartStore(state => state.getTotal())
  const bestDiscount = useCartStore(state => state.bestDiscount)
  const isDiscountLoading = useCartStore(state => state.isDiscountLoading)
  const { money } = useMoney()
  
  return useMemo(() => {
    return {
      subtotal,
      subtotalFormatted: money(subtotal),
      total,
      totalFormatted: money(total),
      discount: bestDiscount,
      discountAmount: bestDiscount ? bestDiscount.amount : 0,
      discountFormatted: bestDiscount ? money(bestDiscount.amount) : money(0),
      isDiscountLoading,
      savings: subtotal - total,
      savingsFormatted: money(subtotal - total)
    }
  }, [subtotal, total, bestDiscount, isDiscountLoading, money])
}

export default useCartStore 